/**
 * Launcher Tank — Escalating threat. Tracking, altitude power, orbital devastation.
 */

import { registerWeapon } from '../weapons';
import { getGroundY } from '../terrain/grid';
import { HOMING_MISSILE, LAUNCHER_GRAV_BOMB } from '../constants';
import { registerTank } from './registry';
import { DEFAULT_CRATER } from '../terrain/crater-shapes';

// Shot 1: Homing Missile — lob → lock → hypersonic dive
const LAUNCHER_SHOT1 = registerWeapon({
  id: 'launcher_homing',
  name: 'Homing Missile',
  description: 'Lobs high, locks on, then dives at hypersonic speed.',
  tier: 'CHEAP',
  rarity: 'common',
  projectileRadius: 6,
  projectileSpeed: 0.60,
  color: 0xFF2288,
  gravity: 0.12,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 14,           // S1 nerf: was 21
  blastRadius: 70,
  knockbackMult: 0.063,
  knockbackRadius: 1.2,
  shakeIntensity: 2.5,
  particleScale: 1.5,
  craterShape: DEFAULT_CRATER,
  windMultiplier: 0.5,
  tracerMaxFrames: 40,
  tracerEndMarker: true,
  edgeguard: { chargeFrames: 30, threshold: 40 },
  onTick(proj, state, fx) {
    proj.behaviorState.flightFrames = (proj.behaviorState.flightFrames || 0) + 1;
    const frames = proj.behaviorState.flightFrames;
    const HM = HOMING_MISSILE;

    // Detonate on lifetime expiry
    if (frames >= HM.LIFETIME) return 'detonate';

    // Copy weapon once for gravity mutation
    if (!proj.behaviorState.weaponCopied) {
      proj.behaviorState.weaponCopied = 1;
      proj.weapon = { ...proj.weapon };
    }

    // Smoke trail — phase-dependent intensity
    const isDive = proj.behaviorState.phase === 3;
    const trailInterval = isDive ? 1 : (HM.TRAIL_INTERVAL + 1);
    if (frames % trailInterval === 0) {
      const puffCount = isDive ? 5 : 3;
      for (let i = 0; i < puffCount; i++) {
        if (isDive) {
          // Dive phase: brighter pink, thicker, ADD + NORMAL mix
          const r = Math.random();
          const trailColor = r < 0.4 ? 0xFF88BB : r < 0.7 ? 0xFF2288 : 0xffffff;
          const useAdd = r > 0.5;
          fx.push({ type: 'particles', method: 'spawnParticle', args: [
            proj.x + (Math.random() - 0.5) * 8,
            proj.y + (Math.random() - 0.5) * 8,
            trailColor,
            0.5, 3.0,     // faster spread
            0.2, 0.5,     // shorter life
            0.3, 0.7,     // larger scale
            0.02, 0.97,
            useAdd, false,
            useAdd ? 0.8 : 0.6,
          ] });
        } else {
          // Lob/lock phases: original dark smoke
          fx.push({ type: 'particles', method: 'spawnParticle', args: [
            proj.x, proj.y, 0x888888,
            0.5, 2.0, 0.3, 0.6, 0.2, 0.5, 0.02, 0.98, false, false, 0.5,
          ] });
        }
      }
    }

    // --- Phase 1: Lob (frames 1..LOB_FRAMES) ---
    if (frames <= HM.LOB_FRAMES) {
      proj.behaviorState.phase = 1;
      proj.weapon.gravity = HM.LOB_GRAVITY;

      // Partial wind resistance during lob
      if (state.wind !== 0) {
        proj.vx -= state.wind * HM.WIND_RESIST;
      }
      return null;
    }

    // --- Phase 2: Lock (frames LOB_FRAMES+1..LOB_FRAMES+LOCK_FRAMES) ---
    if (frames <= HM.LOB_FRAMES + HM.LOCK_FRAMES) {
      // Transition: entering lock phase
      if (proj.behaviorState.phase !== 2) {
        proj.behaviorState.phase = 2;
        proj.weapon.gravity = HM.LOCK_GRAVITY;

        // Bleed off velocity — missile hangs in the air
        proj.vx *= 0.3;
        proj.vy *= 0.1;

        // Find nearest alive opponent and snapshot their position
        let nearestDist = Infinity;
        let targetX = proj.x;
        let targetY = proj.y + 200; // fallback: dive downward
        for (let i = 0; i < state.players.length; i++) {
          if (i === proj.owner) continue;
          const p = state.players[i];
          if (!p.alive) continue;
          const dx = p.x - proj.x;
          const dy = p.y - proj.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < nearestDist) {
            nearestDist = dist;
            targetX = p.x;
            targetY = p.y;
          }
        }
        proj.behaviorState.targetX = targetX;
        proj.behaviorState.targetY = targetY;

        // Lock-on VFX: small flash + screen shake pip
        fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0xffffff, 10] });
        fx.push({ type: 'screen_shake', intensity: 4, decay: 0.85 });
      }

      // Gently drift during lock (near-zero gravity handles it)
      return null;
    }

    // --- Phase 3: Dive (frames after lock) ---
    if (proj.behaviorState.phase !== 3) {
      proj.behaviorState.phase = 3;
      proj.weapon.gravity = HM.DIVE_GRAVITY;

      // Set velocity straight toward target snapshot
      const tx = proj.behaviorState.targetX as number;
      const ty = proj.behaviorState.targetY as number;
      const dx = tx - proj.x;
      const dy = ty - proj.y;
      const dist = Math.sqrt(dx * dx + dy * dy) || 1;
      proj.vx = (dx / dist) * HM.DIVE_SPEED;
      proj.vy = (dy / dist) * HM.DIVE_SPEED;
    }

    // No wind, no steering — pure ballistic line
    return null;
  },
});

// Shot 2: Gravity Bomb — altitude-scaling explosion, skill-based arc
const LAUNCHER_SHOT2 = registerWeapon({
  id: 'launcher_gravity_bomb',
  name: 'Gravity Bomb',
  description: 'Higher arc = bigger boom. Scales with peak altitude.',
  tier: 'UNCOMMON',
  rarity: 'uncommon',
  projectileRadius: 10,
  projectileSpeed: 0.55,
  color: 0xDD1177,
  gravity: 0.11,
  bounces: 0,
  bounceEnergyRetention: 0.85,
  damage: 18,
  blastRadius: 60,
  knockbackMult: 0.05,
  knockbackRadius: 1.3,
  shakeIntensity: 2.0,
  particleScale: 0.8,
  craterShape: DEFAULT_CRATER,
  windMultiplier: 0.4,
  edgeguard: { chargeFrames: 45, threshold: 50 },
  onTick(proj, state, fx) {
    // Track peak altitude (lowest Y = highest point in screen coords)
    if (proj.behaviorState.peakY === undefined || proj.y < proj.behaviorState.peakY) {
      proj.behaviorState.peakY = proj.y;
    }

    // Calculate ground level for altitude fraction (use spawn Y as reference)
    if (proj.behaviorState.groundLevel === undefined) {
      // Find average ground level near the players
      let totalY = 0, count = 0;
      for (const p of state.players) {
        if (!p.alive) continue;
        totalY += getGroundY(p.x);
        count++;
      }
      proj.behaviorState.groundLevel = count > 0 ? totalY / count : 900;
    }

    const groundLevel = proj.behaviorState.groundLevel;
    const peakY = proj.behaviorState.peakY;
    // Altitude as fraction of screen height (higher = lower Y in screen coords)
    const altFraction = Math.max(0, Math.min(1, (groundLevel - peakY) / 1080));

    // Stepped scale factor: <25%→0..0.3, 25-50%→0.3..0.6, 50-75%→0.6..0.85, >75%→0.85..1.0
    let scaledT: number;
    if (altFraction < 0.25) {
      scaledT = (altFraction / 0.25) * 0.3;
    } else if (altFraction < 0.50) {
      scaledT = 0.3 + ((altFraction - 0.25) / 0.25) * 0.3;
    } else if (altFraction < 0.75) {
      scaledT = 0.6 + ((altFraction - 0.50) / 0.25) * 0.25;
    } else {
      scaledT = 0.85 + ((altFraction - 0.75) / 0.25) * 0.15;
    }

    // Copy weapon once for mutation
    if (!proj.behaviorState.weaponCopied) {
      proj.behaviorState.weaponCopied = 1;
      proj.weapon = { ...proj.weapon };
    }

    // Scale stats by altitude
    const gb = LAUNCHER_GRAV_BOMB;
    proj.weapon.damage = Math.round(gb.MIN_DAMAGE + scaledT * (gb.MAX_DAMAGE - gb.MIN_DAMAGE));
    proj.weapon.blastRadius = Math.round(gb.MIN_BLAST + scaledT * (gb.MAX_BLAST - gb.MIN_BLAST));
    proj.weapon.knockbackMult = gb.MIN_KB + scaledT * (gb.MAX_KB - gb.MIN_KB);
    proj.weapon.particleScale = 0.8 + scaledT * 2.2;
    proj.weapon.shakeIntensity = 3.5 - scaledT * 2.0; // lower = more shake

    // Grow radius with altitude (10 → 16)
    proj.radius = Math.round(10 + scaledT * 6);

    // Counteract wind
    if (state.wind !== 0) {
      proj.vx -= state.wind * gb.WIND_RESIST;
    }

    // Light puff trail (every 4 frames) — subtle, single color
    if (proj.behaviorState.flightFrames === undefined) proj.behaviorState.flightFrames = 0;
    proj.behaviorState.flightFrames++;
    if (proj.behaviorState.flightFrames % 4 === 0 && scaledT > 0.2) {
      const glowCount = Math.round(1 + scaledT * 2);
      fx.push({ type: 'particles', method: 'spawnExplosion', args: [proj.x, proj.y, 0xFF44AA, glowCount] });
    }

    return null;
  },
});

registerTank({
  id: 'launcher',
  name: 'Launcher',
  description: 'Escalating threat. Tracking, altitude power, orbital devastation.',
  disabled: false,
  chargeSize: 120,
  visual: { bodyColor: 0xDD1177, glowColor: 0xFF00FF, bodyRadius: 36 },
  ecbRadius: 28,
  landingMultiplier: 0.8,
  shots: [
    { tier: 1, weapon: LAUNCHER_SHOT1, momentumCost: 0, hitlagBonus: 0, label: 'Homing Missile', tierColor: 0xFF2288 },
    { tier: 2, weapon: LAUNCHER_SHOT2, momentumCost: 0, hitlagBonus: 0, label: 'Gravity Bomb', tierColor: 0xDD1177 },
  ],
});
